import type { ReactElement } from 'react';

import { cn } from '@/lib/utils';

type IconProps = {
    className?: string;
};

/**
 * Provider brand marks for the מבזקים rail.
 * Use PNG sources directly - SVG wrappers with external <image> hrefs
 * do not render when loaded via <img src="…svg">.
 */

function ProviderImg({
    src,
    className,
}: {
    src: string;
    className?: string;
}) {
    return (
        <img
            src={src}
            alt=""
            draggable={false}
            aria-hidden
            className={cn('size-8 shrink-0 object-contain', className)}
        />
    );
}

export function AshkelonNewsIcon({ className }: IconProps) {
    return (
        <ProviderImg
            src="/images/favicon-white.png"
            className={className}
        />
    );
}

export function YnetIcon({ className }: IconProps) {
    return <ProviderImg src="/images/providers/ynet.png" className={className} />;
}

export function WallaIcon({ className }: IconProps) {
    return <ProviderImg src="/images/providers/walla.png" className={className} />;
}

/** Mako brand mark on a wide canvas, optically centered for object-contain slots. */
export function MakoIcon({ className }: IconProps) {
    return <ProviderImg src="/images/providers/mako.png" className={className} />;
}

export function KanIcon({ className }: IconProps) {
    return <ProviderImg src="/images/providers/kan.png" className={className} />;
}

export function MaarivIcon({ className }: IconProps) {
    return <ProviderImg src="/images/providers/maariv.png" className={className} />;
}

/** i24NEWS wordmark on a wide canvas for object-contain slots. */
export function I24Icon({ className }: IconProps) {
    return <ProviderImg src="/images/providers/i24.png" className={className} />;
}

function TileMonogram({
    fill,
    label,
    className,
}: IconProps & { fill: string; label: string }) {
    return (
        <span
            className={cn(
                'inline-flex size-8 shrink-0 items-center justify-center rounded-[3px] text-[11px] font-bold leading-none text-white',
                className,
            )}
            style={{ backgroundColor: fill }}
            aria-hidden
        >
            {label}
        </span>
    );
}

export function IsraelHayomIcon(props: IconProps) {
    return <TileMonogram fill="#C8102E" label="ה" {...props} />;
}

export function AshkelonimIcon({ className }: IconProps) {
    return (
        <ProviderImg
            src="/images/providers/ashkelonim.svg"
            className={className}
        />
    );
}

export function KanAshkelonIcon({ className }: IconProps) {
    return (
        <ProviderImg
            src="/images/providers/kan-ashkelon.svg"
            className={className}
        />
    );
}

export function AshqelonNetIcon({ className }: IconProps) {
    return (
        <ProviderImg
            src="/images/providers/ashqelon-net.svg"
            className={className}
        />
    );
}

export function AshkelonNowIcon({ className }: IconProps) {
    return (
        <ProviderImg
            src="/images/providers/ashkelonnow.svg"
            className={className}
        />
    );
}

export function AshkelnayesIcon({ className }: IconProps) {
    return (
        <ProviderImg
            src="/images/providers/ashkelnayes.svg"
            className={className}
        />
    );
}

export function KontraNewsIcon({ className }: IconProps) {
    return (
        <ProviderImg
            src="/images/providers/kontra-news.svg"
            className={className}
        />
    );
}

export type ProviderIcon = (props: IconProps) => ReactElement;

const PROVIDER_ICONS: Record<string, ProviderIcon> = {
    ours: AshkelonNewsIcon,
    ynet: YnetIcon,
    walla: WallaIcon,
    mako: MakoIcon,
    israelhayom: IsraelHayomIcon,
    kan: KanIcon,
    maariv: MaarivIcon,
    i24: I24Icon,
    ashkelonim: AshkelonimIcon,
    'kan-ashkelon': KanAshkelonIcon,
    'ashqelon-net': AshqelonNetIcon,
    ashkelonnow: AshkelonNowIcon,
    ashkelnayes: AshkelnayesIcon,
    'kontra-news': KontraNewsIcon,
};

/** Resolve a provider key to its sidebar icon, or null when unknown. */
export function getProviderIcon(provider: string): ProviderIcon | null {
    return PROVIDER_ICONS[provider] ?? null;
}
